import axios from "axios"

const api = axios.create({
  baseURL: "/api/proxy",
  timeout: 30000,
  withCredentials: true,
})

type RefreshResult = "success" | "invalid" | "unavailable"

let refreshPromise: Promise<RefreshResult> | null = null
let signInRedirectStarted = false

const refreshAccessToken = () => {
  if (!refreshPromise) {
    const pendingRefresh: Promise<RefreshResult> = fetch("/api/auth/refresh", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
    })
      .then((response): RefreshResult => {
        if (response.ok) return "success"
        if ([400, 401, 403].includes(response.status)) return "invalid"
        return "unavailable"
      })
      .catch((error: unknown) => {
        if (process.env.NODE_ENV === "development") {
          console.error("[clientApi] Token refresh failed:", error)
        }
        return "unavailable"
      })

    refreshPromise = pendingRefresh.finally(() => {
      refreshPromise = null
    })
  }

  return refreshPromise
}

api.interceptors.request.use(
  (config) => config,
  (error) => Promise.reject(error)
)

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config

    if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
      originalRequest._retry = true

      const refreshResult = await refreshAccessToken()
      if (refreshResult === "success") {
        signInRedirectStarted = false
        return api(originalRequest)
      }

      // 인증 서버 5xx/timeout은 세션 거절이 아니다. cookie를 보존하고 원래 오류를
      // 호출자에게 돌려 재시도 가능하게 하며, 강제 로그인 이동으로 장애를 증폭하지 않는다.
      if (refreshResult === "unavailable") return Promise.reject(error)
      // Optional personalization on public pages must not force a sign-in navigation.
      if (originalRequest.skipAuthRedirect) return Promise.reject(error)

      // Refresh 실패 시 로그인 페이지로 리다이렉트
      if (typeof window !== "undefined" && !signInRedirectStarted) {
        signInRedirectStarted = true
        const currentPath = `${window.location.pathname}${window.location.search}`
        const locale = currentPath.split("/")[1] || "ko"
        window.location.replace(`/${locale}/sign-in?redirect=${encodeURIComponent(currentPath)}`)
      }
      return Promise.reject(error)
    }

    return Promise.reject(error)
  }
)

export default api
